import type { Recipe } from '../types';

// All-beef meatballs, because the freezer is full of ground beef and not ground
// pork. Two things stand in for the pork: a real panade (breadcrumbs soaked in
// milk) and grated zucchini squeezed dry — the zucchini is also the hidden
// vegetable. Grated carrot in the sauce does the job sugar usually does.

export const bakedMeatballsRedSauce: Recipe = {
  slug: 'baked-meatballs-red-sauce',
  title: 'Baked Meatballs in Red Sauce',
  meal: 'dinner',
  category: 'meal',
  types: ['bake', 'pasta', 'sandwich'],
  cuisines: ['italian'],
  traits: ['freezer-friendly', 'bulk', 'hidden-veg'],
  yield: 'about 48 meatballs — 12 servings',
  activeTime: '45 min',
  totalTime: '1 hr 40 min',
  freezerLife: '4 months',
  difficulty: 2,

  summary:
    'Four pounds of ground beef becomes four dozen meatballs baked on sheet pans, then finished in a big pot of carrot-sweetened tomato sauce. Eat one night, freeze the rest in sauce, flat, four to a bag. Goes on hoagie rolls, over spaghetti, or straight out of a bowl.',

  flavorNotes:
    'Soft, not dense — that is the whole game with all-beef meatballs. The panade and the squeezed zucchini keep them tender; overmixing makes them bouncy. The sauce is plain and a little sweet from the carrot, so the meatballs taste like meatballs and not like a jar. Mild in the pot; red pepper flakes go on at the table for whoever wants them.',

  ingredients: [
    {
      id: 'beef',
      name: 'Ground beef',
      total: '4 lb',
      prep: '80/20 if you have a choice — leaner beef makes dry meatballs',
      fromFreezer: true,
      uses: [{ step: 2, amount: '4 lb' }],
    },
    {
      id: 'panko',
      name: 'Panko breadcrumbs',
      total: '2 cups',
      uses: [{ step: 1, amount: '2 cups', note: 'soaked in the milk — this is the panade' }],
    },
    {
      id: 'milk',
      name: 'Whole milk',
      total: '1 cup',
      uses: [{ step: 1, amount: '1 cup' }],
    },
    {
      id: 'zucchini',
      name: 'Zucchini',
      total: '2 medium',
      prep: 'grated on the big holes, then squeezed hard in a towel',
      uses: [{ step: 1, amount: '2 medium', note: 'about 1 cup after squeezing' }],
    },
    {
      id: 'onion',
      name: 'Yellow onion',
      total: '2 medium',
      prep: '1 grated for the meatballs, 1 diced fine for the sauce',
      uses: [
        { step: 1, amount: '1 medium', note: 'grated, into the meatballs' },
        { step: 4, amount: '1 medium', note: 'diced fine, into the sauce' },
      ],
    },
    {
      id: 'garlic',
      name: 'Garlic',
      total: '12 cloves',
      prep: 'minced — about 2 heads',
      uses: [
        { step: 2, amount: '6 cloves' },
        { step: 4, amount: '6 cloves' },
      ],
    },
    {
      id: 'parmesan',
      name: 'BelGioioso Parmesan',
      total: '1 cup',
      prep: 'grated fine off the block',
      uses: [{ step: 2, amount: '1 cup' }],
    },
    {
      id: 'eggs',
      name: 'Large eggs',
      total: '3',
      uses: [{ step: 2, amount: '3' }],
    },
    {
      id: 'parsley',
      name: 'Flat-leaf parsley',
      total: '½ cup',
      prep: 'chopped, about half a bunch',
      uses: [{ step: 2, amount: '½ cup' }],
    },
    {
      id: 'oregano',
      name: 'Dried oregano',
      total: '4 tsp',
      uses: [
        { step: 2, amount: '2 tsp' },
        { step: 4, amount: '2 tsp' },
      ],
    },
    {
      id: 'salt',
      name: 'Salt',
      total: '2 tbsp',
      uses: [
        { step: 2, amount: '4 tsp', note: 'one teaspoon per pound of beef' },
        { step: 4, amount: '2 tsp', note: 'sauce — taste at the end' },
      ],
    },
    {
      id: 'pepper',
      name: 'Black pepper',
      total: '2 tsp',
      uses: [{ step: 2, amount: '2 tsp' }],
    },
    {
      id: 'olive-oil',
      name: 'Olive oil',
      total: '¼ cup',
      uses: [{ step: 4, amount: '¼ cup' }],
    },
    {
      id: 'carrots',
      name: 'Carrots',
      total: '2 medium',
      prep: 'grated fine — they melt into the sauce',
      uses: [{ step: 4, amount: '2 medium' }],
    },
    {
      id: 'tomato-paste',
      name: 'Tomato paste',
      total: '3 tbsp',
      uses: [{ step: 4, amount: '3 tbsp' }],
    },
    {
      id: 'crushed-tomatoes',
      name: 'Crushed tomatoes, 28 oz cans',
      total: '3 cans',
      prep: 'Red Gold or Dei Fratelli',
      uses: [{ step: 4, amount: '3 cans' }],
    },
    {
      id: 'bay',
      name: 'Bay leaves',
      total: '2',
      uses: [{ step: 4, amount: '2' }],
    },
    {
      id: 'fennel-seed',
      name: 'Fennel seed',
      total: '2 tsp',
      prep: 'crushed with the flat of a knife',
      optional: true,
      optionalKind: 'upgrade',
      optionalWhy:
        'This is the flavor of Italian sausage. With all-beef meatballs it is the single ingredient that makes people ask what you did differently.',
      uses: [{ step: 2, amount: '2 tsp' }],
    },
    {
      id: 'basil',
      name: 'Fresh basil',
      total: '1 handful',
      prep: 'torn, stirred in off the heat',
      optional: true,
      optionalKind: 'upgrade',
      optionalWhy: 'Lifts the sauce from good to noticeably fresh. Dried basil does not do the same thing — skip it rather than substitute.',
      uses: [{ step: 5, amount: '1 handful' }],
    },
    {
      id: 'red-pepper-flakes',
      name: 'Red pepper flakes',
      total: 'to taste',
      optional: true,
      optionalKind: 'garnish',
      optionalWhy: 'Heat stays out of the pot so the whole batch is mild. Shake on at the table.',
      uses: [{ step: 5, amount: 'a pinch per bowl' }],
    },
    {
      id: 'hoagie-rolls',
      name: 'Hoagie rolls',
      total: '1 per sandwich',
      optional: true,
      optionalKind: 'serve-with',
      optionalWhy: 'For meatball subs — the homemade rolls recipe on this site, or a bakery roll. Four meatballs, a ladle of sauce, provolone, under the broiler two minutes.',
      uses: [{ step: 6, amount: '1 per sandwich' }],
    },
    {
      id: 'provolone',
      name: 'Sliced provolone',
      total: '2 slices per sub',
      optional: true,
      optionalKind: 'serve-with',
      optionalWhy: 'Only if you are making subs. Melts better than mozzarella and tastes like the sandwich shop.',
      uses: [{ step: 6, amount: '2 slices per sub' }],
    },
    {
      id: 'spaghetti',
      name: 'Spaghetti',
      total: '1 lb per 4 servings',
      optional: true,
      optionalKind: 'serve-with',
      optionalWhy: 'The other way to eat these. Cook fresh at reheat; pasta does not freeze well in sauce.',
      uses: [{ step: 6, amount: '1 lb per 4 servings' }],
    },
  ],

  steps: [
    {
      n: 1,
      title: 'Panade and the wet vegetables',
      time: '10 min',
      ingredientIds: ['panko', 'milk', 'zucchini', 'onion'],
      body:
        'Heat the oven to 425°F and line two sheet pans with parchment. In a very large bowl, stir the {2 cups|panko} of panko into {1 cup|milk} of milk and leave it — this is the panade, and it needs 5 minutes to turn to paste. Meanwhile grate the {2 medium|zucchini} zucchini on the big holes of a box grater, pile it in a clean towel and wring it out over the sink until it stops dripping. Grate {1 medium|onion} onion the same way, straight into the panade bowl, juice and all. Add the squeezed zucchini.',
      tip:
        'Squeeze harder than feels reasonable. Zucchini is mostly water, and water you leave in ends up steaming the inside of the meatball and making it fall apart in the sauce. You should be left with about a cup of nearly dry shreds.',
    },
    {
      n: 2,
      title: 'Mix with your hands, barely',
      time: '5 min',
      critical: true,
      ingredientIds: ['beef', 'garlic', 'parmesan', 'eggs', 'parsley', 'oregano', 'salt', 'pepper', 'fennel-seed'],
      body:
        'Into the same bowl: {6 cloves|garlic} of minced garlic, {1 cup|parmesan} of grated parmesan, the {3|eggs} eggs, {½ cup|parsley} of parsley, {2 tsp|oregano} of oregano, {4 tsp|salt} of salt, {2 tsp|pepper} of pepper, and the {2 tsp|fennel-seed} of crushed fennel if you are using it. Stir that into a slurry first. Then add the {4 lb|beef} of ground beef and mix with your fingers spread open, lifting and folding, just until you stop seeing streaks of breadcrumb — 30 seconds, no more.',
      tip:
        'Overmixing is the number one meatball mistake and it is easy to do with four pounds. The proteins in ground beef bind tighter the more they are worked, and tight means rubbery. Mix the seasonings into the panade first so they are already spread out before the beef goes in, and stop as soon as it looks even.',
    },
    {
      n: 3,
      title: 'Scoop and bake',
      time: '25 min',
      ingredientIds: [],
      body:
        'Use a 2-tablespoon cookie scoop or wet hands to portion the mix into golf-ball meatballs, about 24 per sheet pan — they can sit close but should not touch. Roll each one lightly between your palms just to smooth it; do not compress. Bake at 425°F for 18 to 20 minutes, swapping the pans top to bottom halfway, until they are browned on top and register 155°F inside. They finish in the sauce, so do not chase 165°F here.',
      tip:
        'Baking instead of pan-frying is what makes four dozen possible in one go without standing over a skillet for an hour. You give up a little crust and get it back when they simmer in the sauce.',
    },
    {
      n: 4,
      title: 'Sauce, while they bake',
      time: '30 min',
      ingredientIds: ['olive-oil', 'onion', 'carrots', 'garlic', 'tomato-paste', 'crushed-tomatoes', 'oregano', 'bay', 'salt'],
      body:
        'In your biggest pot, warm {¼ cup|olive-oil} of olive oil over medium heat. Add the diced {1 medium|onion} onion and the {2 medium|carrots} of grated carrot with a pinch of salt and cook 8 minutes, stirring now and then, until soft and the carrot has gone limp. Add {6 cloves|garlic} of garlic and cook 1 minute. Push everything aside, drop the {3 tbsp|tomato-paste} of tomato paste onto the bare spot and let it darken for 2 minutes — this is the bloom. Stir it in, then add the {3 cans|crushed-tomatoes} of crushed tomatoes, {2 tsp|oregano} of oregano, the {2|bay} bay leaves and {2 tsp|salt} of salt. Rinse each tomato can with a splash of water and add that too. Bring to a lazy bubble and simmer, partly covered, for 20 minutes.',
      tip:
        'The grated carrot is why this sauce has no sugar in it. It melts into the tomato and rounds off the acid the same way a spoon of sugar would, and nobody eating it can find the carrot.',
    },
    {
      n: 5,
      title: 'Marry them',
      time: '20 min',
      ingredientIds: ['basil', 'red-pepper-flakes'],
      body:
        'Slide the baked meatballs into the sauce with any juices from the pans, nudge them under, and simmer uncovered for 20 minutes so they finish cooking and the sauce thickens around them. Fish out the bay leaves. Off the heat, taste for salt and tear in the {1 handful|basil} of basil if you have it. Red pepper flakes go on individual bowls, not in the pot.',
      tip:
        'If the sauce looks thin after 20 minutes, that is the meatball juices — give it another 10 uncovered. If it looks thick, add a splash of water. It should coat a spoon and slide off slowly.',
    },
    {
      n: 6,
      title: 'Serve some, freeze the rest',
      time: '10 min',
      ingredientIds: ['hoagie-rolls', 'provolone', 'spaghetti'],
      body:
        'Tonight: subs on hoagie rolls with provolone under the broiler for 2 minutes, or over spaghetti. Everything else: let the pot cool 30 minutes, then portion 4 meatballs and about ¾ cup of sauce into each quart freezer bag. Press flat, seal with the air pushed out, and stack on a sheet pan in the freezer until solid.',
    },
  ],

  freezing:
    'Four meatballs and about ¾ cup of sauce per quart bag, frozen flat. A dozen bags from one batch. Up to 4 months. Freeze the sauce with the meatballs, never separately — the sauce is what keeps them from drying out on reheat. Cook pasta fresh; toast rolls fresh.',

  reheat: [
    {
      method: 'Stovetop from thawed',
      best: true,
      detail:
        'Thaw the bag in the fridge overnight. Tip into a small pot, cover, and warm over medium-low for 10 minutes, turning the meatballs once. Tastes like the night you made it.',
    },
    {
      method: 'Microwave from frozen',
      detail:
        'Run the sealed bag under warm water for a minute so it releases, then into a bowl, covered, 3 minutes on high, stir, 2 more. Fine for a weeknight.',
    },
    {
      method: 'Oven, for subs',
      detail:
        'Thawed meatballs and sauce into a small baking dish, covered with foil, 350°F for 20 minutes. Split the rolls, load them, top with provolone, and broil 2 minutes.',
    },
  ],

  glossary: [
    {
      term: 'panade',
      plain:
        'Bread or breadcrumbs soaked in milk until they turn to paste. Mixed into ground meat it holds onto moisture during cooking and physically gets in the way of the meat proteins tightening up, so the meatball stays soft. It is the difference between a tender meatball and a dense one, and it matters twice as much with all-beef.',
    },
    {
      term: 'bloom',
      plain:
        'Cooking tomato paste in fat for a couple of minutes before adding liquid. It goes from bright red to brick red and loses its raw, tinny taste. Skip it and the sauce tastes like the can.',
    },
    {
      term: 'lazy bubble',
      plain:
        'A simmer where a bubble breaks the surface every second or two — not a rolling boil. Tomato sauce at a hard boil spits everywhere and scorches on the bottom of the pot.',
    },
  ],

  inspiration: [
    { label: 'Jessica Gavin — grandma\'s Italian meatballs', url: 'https://www.jessicagavin.com/grandmas-italian-meatball-recipe/' },
    { label: 'Two Kooks in the Kitchen — all-purpose Italian meatballs', url: 'https://twokooksinthekitchen.com/great-all-purpose-italian-meatballs/' },
    { label: 'Thriving Home — baked Italian meatballs, freezer meal', url: 'https://thrivinghomeblog.com/baked-italian-meatballs-freezer-meal/' },
  ],

  whereWeVeered:
    'Most of these recipes are beef-and-pork. Ours is all beef because that is what the freezer holds, so we leaned on a bigger panade and added grated, squeezed zucchini for moisture — which also happens to hide a vegetable in every meatball. Grated carrot replaces the sugar most red sauces call for. Scaled to four pounds and baked on sheet pans because nobody is pan-frying 48 meatballs. Fennel seed, which the pork would have brought, is offered as the one upgrade worth buying.',
};
